import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { Button, Input, NavHeader } from '../../components';
import { SendVerificationModal } from '../../components/Modals/ModalSendEmail';
import { useApp, useAuth } from '../../context';
import { sendCodeEmail } from '../../controller/EmailController';
import { ICreate, ILogin, UserController } from '../../controller/UserController';
import { useAlert } from '../../hooks/useAlert';
import { TutorialType, TypeContact, labelExternalContacts } from '../../types/enums';
import { encryptPassword } from '../../utils/encryptPassword';
import { Container, Content, Form, FormGrid } from './styles';
import { ModalTermConditions } from '../../components/Modals/ModalTermConditions';
import { decryptCode } from '../../utils/crypto';
import { useConfig } from '../../context/configCustom';
import JoyrideTour from '../../components/JoyrideTour';
import TitlePage from '../../components/TitlePage';
import { useTutorialProgress } from '../../context/tutorialProgress';

export function Register() {
  const customAlert = useAlert();
  const { tutorialProgress } = useTutorialProgress();
  const app = useApp();
  const { login } = useAuth();
  const { pathResourceEmail } = useConfig();
  const navigate = useNavigate();
  const [isRegistering, setIsRegistering] = useState(false);
  const [isAcceptedTermConditions, setIsAcceptedTermConditions] = useState(false);
  const [isOpenTermConditions, setIsOpenTermConditions] = useState(false);
  const [expectedCode, setExpectedCode] = useState('');
  const [sentEmail, setSentEmail] = useState(false);
  const [displaySendEmailModal, setDisplaySendEmailModal] = useState(false);
  const [runningTutorial, setRunningTutorial] = useState(false);
  const [defaultValues] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });

  const registerSchema = yup.object().shape({
    email: yup.string().required('O campo e-mail é obrigatório').email('Digite um e-mail válido'),
    password: yup
      .string()
      .required('O campo senha é obrigatório')
      .min(6, 'Senha deve ter no mínimo 6 dígitos'),
    confirmPassword: yup.string().oneOf([yup.ref('password'), null], 'Senhas não coincidem'),
  });

  const {
    handleSubmit,
    formState: { errors },
    control,
    setError,
  } = useForm({
    defaultValues,
    reValidateMode: 'onChange',
    resolver: yupResolver(registerSchema),
  });

  const [email, password] = useWatch({
    control,
    name: ['email', 'password'],
  });

  const handleSetDisplaySendEmailModal = async () => {
    try {
      if (!pathResourceEmail) {
        customAlert.show({
          type: 'error',
          message: 'Não foi possível enviar o e-mail, tente novamente!',
        });
        return;
      }
      await UserController.validateRegister({
        email,
      });
      setDisplaySendEmailModal(true);
      if (!sentEmail) {
        app.setLoading(true);
        sendCodeEmail({
          type: 'activeAccount',
          to: email,
          pathResource: pathResourceEmail,
        })
          .then((resCode) => {
            setExpectedCode(decryptCode(resCode));
            setSentEmail(true);
          })
          .catch(() => {
            customAlert.show({
              type: 'error',
              message: 'Não foi possível enviar o e-mail, tente novamente!',
            });
          })
          .finally(() => app.setLoading(false));
      }
    } catch (error: any) {
      if (error?.response?.data?.detail === 'EMAIL_ALREADY_REGISTERED') {
        setError('email', {
          message: 'Email já cadastrado',
        });
      } else {
        customAlert.show({
          type: 'error',
          message: 'Um erro inesperado ocorreu, tente novamente.',
        });
      }
    }
  };

  const register = async () => {
    if (isRegistering) return;
    const user: ICreate = {
      email,
      password: encryptPassword(password),
      acceptedPrivacyPolicy: true,
      acceptedTermConditions: isAcceptedTermConditions,
    };
    setIsRegistering(true);
    UserController.create(user)
      .then(async (uid) => {
        customAlert.show({ type: 'success', message: 'Usuário registrado com sucesso' });
        await UserController.generateUrl(uid);
        const form: ILogin = {
          email: user.email,
          password,
        };
        await login(form)
          .then(() => {
            navigate('/complementRegister');
          })
          .catch(() => {
            customAlert.show({
              type: 'error',
              message: 'Não foi possível realizar a autenticação',
            });
          });
      })
      .finally(() => {
        setIsRegistering(false);
      });
  };

  const handleAcceptTermConditions = async () => {
    if (isAcceptedTermConditions) {
      handleSetDisplaySendEmailModal();
    } else {
      setIsAcceptedTermConditions(true);
      handleSetDisplaySendEmailModal();
    }
  };

  const steps = [
    {
      target: '#start-item',
      content:
        'Esta página é destinada a criação de uma nova conta. Preencha os campos com as informações solicitadas.',
      disableBeacon: true,
    },
    {
      target: '#email-input',
      content: 'Insira o e-mail que deseja cadastrar.',
    },
    {
      target: '#password-input',
      content: 'Insira a senha que deseja cadastrar.',
    },
    {
      target: '#confirm-password-input',
      content: 'Confirme a senha que deseja cadastrar.',
    },
    {
      target: '#button',
      content:
        'Após preencher os campos, clique no botão "Avançar" para prosseguir com o cadastro.',
    },
    {
      target: '#confirm-term-conditions',
      content:
        'Leia e aceite os termos de condições para prosseguir com o cadastro. Clique no botão "Aceitar" para continuar.',
      delay: 1000,
    },
  ];

  const handleStepChange = (target: string) => {
    if (target === '#confirm-term-conditions') {
      setIsOpenTermConditions(true);
    } else {
      setIsOpenTermConditions(false);
    }
  };

  const handleOpenModalTermConditions = async () => {
    if (isAcceptedTermConditions) {
      handleSetDisplaySendEmailModal();
      return;
    } else {
      try {
        await UserController.validateRegister({
          email,
        });
      } catch (error: any) {
        if (error?.response?.data?.detail === 'EMAIL_ALREADY_REGISTERED') {
          setError('email', {
            message: 'Email já cadastrado',
          });
        } else {
          customAlert.show({
            type: 'error',
            message: 'Um erro inesperado ocorreu, tente novamente.',
          });
        }
        return;
      }
    }
    setIsOpenTermConditions(true);
  };
  useEffect(() => {
    if (!tutorialProgress?.includes(TutorialType.REGISTER)) {
      setRunningTutorial(true);
    }
  }, [tutorialProgress]);
  return (
    <Container>
      <JoyrideTour
        steps={steps}
        type={TutorialType.REGISTER}
        run={runningTutorial}
        onCallbackFinished={() => {
          setRunningTutorial(false);
          setIsOpenTermConditions(false);
        }}
        onStepChange={handleStepChange}
      />
      <NavHeader typeHeader="default" />
      <div className="title-page">
        <TitlePage
          title="Cadastro"
          onRequestOpen={() => {
            setRunningTutorial(true);
          }}
          id="start-item"
        />
      </div>
      <Content>
        <Form>
          <div className="formDiv">
            <Input
              id="email-input"
              name="email"
              control={control}
              type="text"
              label="E-mail"
              errorText={errors.email?.message}
              variant="standard"
              fullWidth
              disabled={isAcceptedTermConditions}
              error={!!errors.email}
            />
            <FormGrid>
              <div>
                <Input
                  id="password-input"
                  name="password"
                  control={control}
                  type="password"
                  label="Senha"
                  errorText={errors.password?.message}
                  variant="standard"
                  fullWidth
                  disabled={isAcceptedTermConditions}
                  error={!!errors.password}
                />
              </div>
              <div>
                <Input
                  id="confirm-password-input"
                  name="confirmPassword"
                  control={control}
                  type="password"
                  label="Confirmar senha"
                  errorText={errors.confirmPassword?.message}
                  variant="standard"
                  disabled={errors.confirmPassword?.message ? false : isAcceptedTermConditions}
                  fullWidth
                  error={!!errors.confirmPassword}
                />
              </div>
            </FormGrid>
            {/* <SocialLogin /> */}
          </div>
          <div className="buttonDiv">
            <div>
              <Button
                id="button"
                dimensions={{ width: 208, height: 36 }}
                text="Avançar"
                onClick={() => {
                  handleSubmit(handleOpenModalTermConditions)();
                }}
              />
            </div>
          </div>
        </Form>
      </Content>
      <SendVerificationModal
        isRequesting={isRegistering}
        isOpen={displaySendEmailModal}
        onRequestClose={() => {
          setDisplaySendEmailModal(false);
        }}
        onCodeChange={setExpectedCode}
        data={{
          codeExpected: expectedCode,
          contactValue: email,
          contactLabel: labelExternalContacts[TypeContact.EMAIL],
        }}
        onCallback={register}
      />
      {isOpenTermConditions === true && (
        <ModalTermConditions
          isOpen={isOpenTermConditions}
          onRequestClose={() => setIsOpenTermConditions(false)}
          onCallBack={() => handleAcceptTermConditions()}
        />
      )}
    </Container>
  );
}
